import { Link } from 'react-router-dom';
import { Button, Card, Typography, Badge } from '../components/ui';
import AnimatedText from '../components/AnimatedText';

const Home = () => {
  return (
    <div
      className="min-h-screen"
      style={{
        background: `linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-tertiary) 100%)`,
        color: 'var(--text-primary)',
      }}
    >
      {/* Hero Section */}
      <div className="container mx-auto px-4 py-8 sm:py-12 lg:py-16">
        <div className="text-center mb-12 sm:mb-16">
          <Typography variant="h1" align="center" className="mb-4 sm:mb-6">
            From Intuition to Execution
            <div style={{ color: 'var(--accent)' }}>
              <AnimatedText
                words={[
                  'SudoSys',
                  'Innovation',
                  'Excellence',
                  'Solutions',
                  'Technology',
                ]}
                interval={2000}
              />
            </div>
          </Typography>
          <Typography
            variant="subtitle1"
            align="center"
            color="secondary"
            className="max-w-3xl mx-auto leading-relaxed mb-6 sm:mb-8"
          >
            We deliver cutting-edge software development and AI solutions that
            transform businesses and drive digital innovation. From custom
            applications to intelligent automation, we're your trusted
            technology partner.
          </Typography>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-6 sm:mb-8">
            <Link to="/services">
              <Button variant="primary" size="lg" className="w-full sm:w-auto">
                Explore Services
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                variant="secondary"
                size="lg"
                className="w-full sm:w-auto"
              >
                Get In Touch
              </Button>
            </Link>
          </div>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
            <Badge variant="primary">Software Development</Badge>
            <Badge variant="success">AI Solutions</Badge>
            <Badge variant="warning">Digital Transformation</Badge>
          </div>
        </div>

        {/* Our Products Section */}
        <div className="mb-12 sm:mb-16">
          <div className="text-center mb-8 sm:mb-12">
            <h2
              className="text-2xl sm:text-3xl font-bold mb-4"
              style={{ color: 'var(--text-primary)' }}
            >
              Our <span style={{ color: 'var(--accent)' }}>Products</span>
            </h2>
            <Typography
              variant="subtitle1"
              align="center"
              color="secondary"
              className="max-w-2xl mx-auto"
            >
              In addition to our consulting services, we develop innovative
              products designed to solve real business challenges.
            </Typography>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-8">
            {/* Sudo-Credit Product Card */}
            <Card
              padding="lg"
              className="transition-all duration-300 transform hover:scale-105 ring-2"
              style={{
                boxShadow: `0 20px 40px rgba(233, 110, 0, 0.2)`,
                borderColor: 'var(--accent)',
              }}
            >
              <Badge variant="primary" className="mb-4">
                Available Now
              </Badge>
              <h3
                className="text-2xl sm:text-3xl font-bold mb-3"
                style={{ color: 'var(--accent)' }}
              >
                📱 Sudo-Credit
              </h3>
              <Typography variant="body1" className="mb-4" color="secondary">
                A comprehensive transaction management and billing solution
                built for small to medium-sized businesses. Manage unlimited
                transactions, create professional bills, and grow your business
                with confidence.
              </Typography>
              <div className="mb-6">
                <Typography
                  variant="h4"
                  style={{ color: 'var(--accent)' }}
                  className="mb-3"
                >
                  Key Features:
                </Typography>
                <ul className="list-none p-0 space-y-2">
                  <li className="flex items-center gap-2">
                    <span style={{ color: 'var(--accent)' }}>✓</span>
                    <Typography variant="body2">
                      Unlimited Transactions
                    </Typography>
                  </li>
                  <li className="flex items-center gap-2">
                    <span style={{ color: 'var(--accent)' }}>✓</span>
                    <Typography variant="body2">Web & Mobile Access</Typography>
                  </li>
                  <li className="flex items-center gap-2">
                    <span style={{ color: 'var(--accent)' }}>✓</span>
                    <Typography variant="body2">Advanced Analytics</Typography>
                  </li>
                  <li className="flex items-center gap-2">
                    <span style={{ color: 'var(--accent)' }}>✓</span>
                    <Typography variant="body2">
                      Role-Based Access Control
                    </Typography>
                  </li>
                </ul>
              </div>
              <Link to="/products">
                <Button
                  className="font-semibold py-2 cursor-pointer w-full"
                  style={{
                    backgroundColor: 'var(--accent)',
                    color: 'white',
                  }}
                >
                  Learn More
                </Button>
              </Link>
            </Card>

            {/* Coming Soon Card */}
            <Card
              padding="lg"
              className="transition-all duration-300 transform hover:scale-105 opacity-80"
            >
              <Badge variant="secondary" className="mb-4">
                Coming Soon
              </Badge>
              <h3
                className="text-2xl sm:text-3xl font-bold mb-3"
                style={{ color: 'var(--text-secondary)' }}
              >
                🚀 More Products
              </h3>
              <Typography variant="body1" className="mb-4" color="secondary">
                We're actively developing innovative solutions to expand our
                product suite. Stay tuned for exciting new products designed to
                empower your business even further.
              </Typography>
              <div className="mb-6">
                <Typography
                  variant="h4"
                  style={{ color: 'var(--text-secondary)' }}
                  className="mb-3"
                >
                  Coming Next:
                </Typography>
                <ul className="list-none p-0 space-y-2">
                  <li className="flex items-center gap-2">
                    <span style={{ color: 'var(--text-secondary)' }}>◆</span>
                    <Typography variant="body2" color="secondary">
                      Enterprise Solutions
                    </Typography>
                  </li>
                  <li className="flex items-center gap-2">
                    <span style={{ color: 'var(--text-secondary)' }}>◆</span>
                    <Typography variant="body2" color="secondary">
                      AI-Powered Analytics
                    </Typography>
                  </li>
                  <li className="flex items-center gap-2">
                    <span style={{ color: 'var(--text-secondary)' }}>◆</span>
                    <Typography variant="body2" color="secondary">
                      Integration Platform
                    </Typography>
                  </li>
                  <li className="flex items-center gap-2">
                    <span style={{ color: 'var(--text-secondary)' }}>◆</span>
                    <Typography variant="body2" color="secondary">
                      And more...
                    </Typography>
                  </li>
                </ul>
              </div>
              <Button
                className="font-semibold py-2 cursor-pointer w-full"
                style={{
                  backgroundColor: 'transparent',
                  color: 'var(--accent)',
                  border: '2px solid var(--accent)',
                }}
                disabled
              >
                Coming Soon
              </Button>
            </Card>
          </div>
        </div>

        {/* Services Overview */}
        <div className="mb-12 sm:mb-16">
          <h2
            className="text-2xl sm:text-3xl font-bold text-center mb-8 sm:mb-12"
            style={{ color: 'var(--text-primary)' }}
          >
            Our Expertise
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <div
              className="rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all hover:scale-105 border"
              style={{
                backgroundColor: 'var(--bg-primary)',
                borderColor: 'var(--border)',
                boxShadow: 'var(--shadow)',
              }}
            >
              <h3
                className="text-lg sm:text-xl font-semibold mb-3"
                style={{ color: 'var(--text-primary)' }}
              >
                💻 Custom Software Development
              </h3>
              <p
                style={{ color: 'var(--text-secondary)' }}
                className="text-sm sm:text-base"
              >
                Full-stack web applications, mobile apps, and enterprise
                solutions built with modern technologies and best practices.
              </p>
            </div>
            <div
              className="rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all hover:scale-105 border"
              style={{
                backgroundColor: 'var(--bg-primary)',
                borderColor: 'var(--border)',
                boxShadow: 'var(--shadow)',
              }}
            >
              <h3
                className="text-lg sm:text-xl font-semibold mb-3"
                style={{ color: 'var(--text-primary)' }}
              >
                🤖 AI & Machine Learning
              </h3>
              <p
                style={{ color: 'var(--text-secondary)' }}
                className="text-sm sm:text-base"
              >
                Intelligent automation, predictive analytics, natural language
                processing, and custom AI models tailored to your business
                needs.
              </p>
            </div>
            <div
              className="rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all hover:scale-105 border"
              style={{
                backgroundColor: 'var(--bg-primary)',
                borderColor: 'var(--border)',
                boxShadow: 'var(--shadow)',
              }}
            >
              <h3
                className="text-lg sm:text-xl font-semibold mb-3"
                style={{ color: 'var(--text-primary)' }}
              >
                📊 Data Analytics
              </h3>
              <p
                style={{ color: 'var(--text-secondary)' }}
                className="text-sm sm:text-base"
              >
                Business intelligence, data visualization, ETL pipelines, and
                data-driven decision support systems.
              </p>
            </div>
            <div
              className="rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all hover:scale-105 border"
              style={{
                backgroundColor: 'var(--bg-primary)',
                borderColor: 'var(--border)',
                boxShadow: 'var(--shadow)',
              }}
            >
              <h3
                className="text-lg sm:text-xl font-semibold mb-3"
                style={{ color: 'var(--text-primary)' }}
              >
                🔧 DevOps & Automation
              </h3>
              <p
                style={{ color: 'var(--text-secondary)' }}
                className="text-sm sm:text-base"
              >
                CI/CD pipelines, infrastructure as code, monitoring solutions,
                and deployment automation for reliable software delivery.
              </p>
            </div>
            <div
              className="rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all hover:scale-105 border"
              style={{
                backgroundColor: 'var(--bg-primary)',
                borderColor: 'var(--border)',
                boxShadow: 'var(--shadow)',
              }}
            >
              <h3
                className="text-lg sm:text-xl font-semibold mb-3"
                style={{ color: 'var(--text-primary)' }}
              >
                📱 Digital Transformation
              </h3>
              <p
                style={{ color: 'var(--text-secondary)' }}
                className="text-sm sm:text-base"
              >
                Legacy system modernization, digital strategy consulting, and
                technology roadmap planning for competitive advantage.
              </p>
            </div>
          </div>
        </div>

        {/* Why Choose Us Section */}
        <div className="mb-12 sm:mb-16">
          <Typography variant="h2" align="center" className="mb-8 sm:mb-12">
            Why Choose SudoSys?
          </Typography>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 max-w-4xl mx-auto">
            <Card className="text-center" padding="lg">
              <h3
                className="text-lg sm:text-xl font-semibold mb-4"
                style={{ color: 'var(--text-primary)' }}
              >
                🎯 Results-Driven Approach
              </h3>
              <Typography variant="body1" color="secondary">
                We focus on delivering measurable business value through
                technology, ensuring every solution aligns with your strategic
                objectives.
              </Typography>
            </Card>
            <Card className="text-center" padding="lg">
              <h3
                className="text-lg sm:text-xl font-semibold mb-4"
                style={{ color: 'var(--text-primary)' }}
              >
                ⚡ Agile Development
              </h3>
              <Typography variant="body1" color="secondary">
                Our agile methodology ensures rapid delivery, continuous
                feedback, and adaptability to changing requirements.
              </Typography>
            </Card>
            <Card className="text-center" padding="lg">
              <h3
                className="text-lg sm:text-xl font-semibold mb-4"
                style={{ color: 'var(--text-primary)' }}
              >
                🛡️ Enterprise Security
              </h3>
              <Typography variant="body1" color="secondary">
                Security-first approach with industry best practices, compliance
                standards, and robust data protection measures.
              </Typography>
            </Card>
            <Card className="text-center" padding="lg">
              <h3
                className="text-lg sm:text-xl font-semibold mb-4"
                style={{ color: 'var(--text-primary)' }}
              >
                🤝 Partnership Focus
              </h3>
              <Typography variant="body1" color="secondary">
                We build long-term partnerships, providing ongoing support,
                maintenance, and strategic technology guidance.
              </Typography>
            </Card>
          </div>
        </div>

        {/* Newsletter Section */}
        {/* <div className="mb-12 sm:mb-16">
          <Newsletter
            title="Stay Updated with Tech Insights"
            description="Join our newsletter to receive the latest updates on emerging technologies, development best practices, and industry insights."
            source="homepage"
            variant="default"
          />
        </div> */}

        {/* Call to Action */}
        <div className="text-center">
          <Card className="max-w-2xl mx-auto" padding="lg">
            <Typography variant="h3" align="center" className="mb-4">
              Ready to Transform Your Business?
            </Typography>
            <Typography
              variant="body1"
              color="secondary"
              className="mb-6"
              align="center"
            >
              Let's discuss how SudoSys can help you leverage technology to
              achieve your goals. Contact us today for a free consultation.
            </Typography>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/contact">
                <Button
                  variant="primary"
                  size="lg"
                  className="w-full sm:w-auto"
                >
                  Start Your Project
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Home;
